/**
 * MapInterpolation — scroll-synchronised camera interpolation for map layers.
 *
 * Given an ordered list of MapWaypoints (each tagged with a scrollPosition),
 * interpolates lng, lat, zoom, bearing and pitch for any timer value.
 * Uses the same Easing functions as the rest of the Scrolly keyframe system.
 */

import { Easings } from "@scrolly/renderer";
import type { PossibleEasings } from "@scrolly/post-types";
import type { MapMarker, MapWaypoint } from "@scrolly/post-types";

export interface InterpolatedCamera {
    lng: number;
    lat: number;
    zoom: number;
    bearing: number;
    pitch: number;
}

/** Lerp that handles longitude wrapping across the ±180° antimeridian. */
function lerpLng(a: number, b: number, t: number): number {
    let delta = b - a;
    // Take the shortest path across ±180
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    let result = a + delta * t;
    // Normalise to [-180, 180]
    if (result > 180) result -= 360;
    if (result < -180) result += 360;
    return result;
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/**
 * Interpolate camera parameters between waypoints for a given scroll timer.
 *
 * - Before first waypoint: returns first waypoint's values.
 * - After last waypoint: returns last waypoint's values.
 * - Between two waypoints: eased interpolation using the **destination** waypoint's easing.
 */
export function interpolateWaypoints(
    waypoints: MapWaypoint[],
    timer: number,
): InterpolatedCamera {
    if (waypoints.length === 0) {
        return { lng: 0, lat: 0, zoom: 2, bearing: 0, pitch: 0 };
    }
    if (waypoints.length === 1 || timer <= waypoints[0].scrollPosition) {
        const w = waypoints[0];
        return {
            lng: w.lng,
            lat: w.lat,
            zoom: w.zoom,
            bearing: w.bearing ?? 0,
            pitch: w.pitch ?? 0,
        };
    }
    if (timer >= waypoints[waypoints.length - 1].scrollPosition) {
        const w = waypoints[waypoints.length - 1];
        return {
            lng: w.lng,
            lat: w.lat,
            zoom: w.zoom,
            bearing: w.bearing ?? 0,
            pitch: w.pitch ?? 0,
        };
    }

    // Find bracketing waypoints
    let fromIdx = 0;
    for (let i = 1; i < waypoints.length; i++) {
        if (waypoints[i].scrollPosition >= timer) {
            fromIdx = i - 1;
            break;
        }
    }
    const from = waypoints[fromIdx];
    const to = waypoints[fromIdx + 1];

    const range = to.scrollPosition - from.scrollPosition;
    const rawT = range > 0 ? (timer - from.scrollPosition) / range : 1;

    // Apply easing from destination waypoint (default quadInOut)
    const easingKey = (to.easing ?? "quadInOut") as PossibleEasings;
    const easingFn = Easings[easingKey] ?? Easings.quadInOut;
    const t = easingFn(Math.max(0, Math.min(1, rawT)));

    return {
        lng: lerpLng(from.lng, to.lng, t),
        lat: lerp(from.lat, to.lat, t),
        zoom: lerp(from.zoom, to.zoom, t) - (to.bogen ? bogenTiefe(from, to) * Math.sin(Math.PI * t) : 0),
        bearing: lerp(from.bearing ?? 0, to.bearing ?? 0, t),
        pitch: lerp(from.pitch ?? 0, to.pitch ?? 0, t),
    };
}

/**
 * Return markers that should be visible at the given timer value.
 * Shows markers from the closest waypoint (by scroll position).
 */
export function getActiveMarkers(
    waypoints: MapWaypoint[],
    timer: number,
): MapMarker[] {
    if (waypoints.length === 0) return [];

    let closest = waypoints[0];
    let minDist = Math.abs(timer - closest.scrollPosition);

    for (let i = 1; i < waypoints.length; i++) {
        const dist = Math.abs(timer - waypoints[i].scrollPosition);
        if (dist < minDist) {
            minDist = dist;
            closest = waypoints[i];
        }
    }

    return closest.markers ?? [];
}

/**
 * Bogen (Pro-Editor-Karte, 09.10.2026): Wie weit muss die Kamera zwischen
 * zwei Orten herauszoomen, damit beide gleichzeitig ins Bild passen? 0, wenn
 * beide Zoomstufen das ohnehin schon schaffen. Grob gerechnet für ein
 * Handybild (~400 px breit, Strecke auf ~60 % der Breite).
 */
export function bogenTiefe(a: MapWaypoint, b: MapWaypoint): number {
    let dLng = Math.abs(b.lng - a.lng);
    if (dLng > 180) dLng = 360 - dLng;
    const breite = Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
    const strecke = Math.max(dLng * breite, Math.abs(b.lat - a.lat), 1e-4);
    const passt = Math.log2(170 / strecke);
    return Math.max(0, Math.min(a.zoom, b.zoom) - passt);
}

/**
 * Wegpunkte mit `anteil` (Pro-Editor-Karte) an die Laufzeit der Ebene binden:
 * scrollPosition = from + anteil × (to − from), sortiert nach Ankunft.
 * Wegpunkte ohne `anteil` (Studio-Karten) bleiben, wie sie sind.
 */
export function wegpunkteInLaufzeit(
    waypoints: MapWaypoint[],
    from: number,
    to: number,
): MapWaypoint[] {
    if (!waypoints.some((w) => typeof w.anteil === "number")) return waypoints;
    return waypoints
        .map((w) =>
            typeof w.anteil === "number"
                ? { ...w, scrollPosition: from + Math.max(0, Math.min(1, w.anteil)) * (to - from) }
                : w,
        )
        .sort((a, b) => a.scrollPosition - b.scrollPosition);
}

/**
 * Fahrt per Keyframes (Pro-Editor-Karte, Jan 10.10.2026): Der Kanal
 * `mapPlace` liefert den Ort als Kommazahl — 1,4 heißt „40 % des Wegs von
 * Ort 1 zu Ort 2“. Die Kurve (Easing) steckt schon im Keyframe; hier wird
 * deshalb LINEAR zwischen den beiden Orten gemischt (sonst doppelt gebremst).
 * Reihenfolge = Reihenfolge der Ortsliste.
 */
export function kameraAmOrt(waypoints: MapWaypoint[], ort: number): InterpolatedCamera {
    if (waypoints.length === 0) return { lng: 0, lat: 0, zoom: 2, bearing: 0, pitch: 0 };
    const letzter = waypoints.length - 1;
    const o = Math.max(0, Math.min(letzter, Number.isFinite(ort) ? ort : 0));
    const i = Math.min(Math.floor(o), Math.max(0, letzter - 1));
    const from = waypoints[i];
    const to = waypoints[Math.min(i + 1, letzter)];
    const t = to === from ? 0 : o - i;
    return {
        lng: lerpLng(from.lng, to.lng, t),
        lat: lerp(from.lat, to.lat, t),
        zoom: lerp(from.zoom, to.zoom, t) - (to.bogen ? bogenTiefe(from, to) * Math.sin(Math.PI * t) : 0),
        bearing: lerp(from.bearing ?? 0, to.bearing ?? 0, t),
        pitch: lerp(from.pitch ?? 0, to.pitch ?? 0, t),
    };
}

/** Nadeln des Ortes, dem die Kamera gerade am nächsten ist. */
export function nadelnAmOrt(waypoints: MapWaypoint[], ort: number): MapMarker[] {
    if (waypoints.length === 0) return [];
    const i = Math.max(0, Math.min(waypoints.length - 1, Math.round(Number.isFinite(ort) ? ort : 0)));
    return waypoints[i].markers ?? [];
}

/**
 * Kamera + Nadeln für ein Bild: mit Fahrt-Keyframes (`ort` gesetzt) nach Ort,
 * sonst wie bisher nach Zeit (`anteil` bzw. scrollPosition — Studio-Karten).
 */
export function kartenBild(
    waypoints: MapWaypoint[],
    from: number,
    to: number,
    timer: number,
    ort: number | undefined,
): { kamera: InterpolatedCamera; nadeln: MapMarker[] } {
    if (typeof ort === "number") {
        return { kamera: kameraAmOrt(waypoints, ort), nadeln: nadelnAmOrt(waypoints, ort) };
    }
    const wegpunkte = wegpunkteInLaufzeit(waypoints, from, to);
    return { kamera: interpolateWaypoints(wegpunkte, timer), nadeln: getActiveMarkers(wegpunkte, timer) };
}

/** Punkt auf dem Großkreis (kürzester Weg über die Erdkugel) zwischen a und b. */
function grosskreis(a: MapWaypoint, b: MapWaypoint, t: number): [number, number] {
    const r = Math.PI / 180;
    const [l1, p1, l2, p2] = [a.lng * r, a.lat * r, b.lng * r, b.lat * r];
    const d = 2 * Math.asin(Math.sqrt(
        Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2,
    ));
    if (d < 1e-9) return [a.lng, a.lat];
    const A = Math.sin((1 - t) * d) / Math.sin(d);
    const B = Math.sin(t * d) / Math.sin(d);
    const x = A * Math.cos(p1) * Math.cos(l1) + B * Math.cos(p2) * Math.cos(l2);
    const y = A * Math.cos(p1) * Math.sin(l1) + B * Math.cos(p2) * Math.sin(l2);
    const z = A * Math.sin(p1) + B * Math.sin(p2);
    return [Math.atan2(y, x) / r, Math.atan2(z, Math.sqrt(x * x + y * y)) / r];
}

/**
 * Route der Karte (Jan 10.10.2026: „Route zeichnen“) als Linienzug durch die
 * Orte — gerade oder im Bogen über die Erdkrümmung (wie ein Flug). Mit `bis`
 * (Ort als Kommazahl aus der Fahrt-Spur) wächst sie mit der Fahrt mit; ohne
 * steht sie ganz. Längengrade werden „ausgerollt“, damit eine Linie über die
 * Datumsgrenze nicht einmal quer über die Welt springt.
 */
export function routenLinie(
    waypoints: MapWaypoint[],
    bis: number | undefined,
    bogen: boolean,
    schritte = 48,
): Array<[number, number]> {
    if (waypoints.length < 2) return [];
    const ende = Math.max(0, Math.min(waypoints.length - 1, typeof bis === "number" && Number.isFinite(bis) ? bis : waypoints.length - 1));
    const punkt = (a: MapWaypoint, b: MapWaypoint, t: number): [number, number] =>
        bogen ? grosskreis(a, b, t) : [lerpLng(a.lng, b.lng, t), lerp(a.lat, b.lat, t)];
    const linie: Array<[number, number]> = [[waypoints[0].lng, waypoints[0].lat]];
    for (let i = 0; i < waypoints.length - 1 && i < ende; i++) {
        const anteil = Math.min(1, ende - i);
        for (let s = 1; s <= schritte; s++) {
            const t = s / schritte;
            if (t >= anteil) {
                linie.push(punkt(waypoints[i], waypoints[i + 1], anteil));
                break;
            }
            linie.push(punkt(waypoints[i], waypoints[i + 1], t));
        }
    }
    // Längengrade ausrollen (−180/180-Sprung vermeiden)
    for (let k = 1; k < linie.length; k++) {
        let d = linie[k][0] - linie[k - 1][0];
        while (d > 180) { linie[k][0] -= 360; d -= 360; }
        while (d < -180) { linie[k][0] += 360; d += 360; }
    }
    return linie;
}
